import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuthStore } from "../lib/store";
import { BASE_URL } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import {
  Palette,
  Upload,
  Image as ImageIcon,
  Type,
  Settings,
  Save,
  Trash2,
  Plus,
  Check,
  X,
  Video,
  Hash,
  Loader2,
  Sparkles,
  Layout,
  FileVideo,
} from "lucide-react";
import MediaPickerModal, { MediaPickerButton } from "../components/MediaPickerModal";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Available fonts
const FONTS = [
  "Inter", "Roboto", "Open Sans", "Montserrat", "Poppins",
  "Playfair Display", "Lora", "Oswald", "Raleway", "Source Sans Pro"
];

// Video styles
const VIDEO_STYLES = [
  { value: "cinematic", label: "Cinematic" },
  { value: "anime", label: "Anime" },
  { value: "realistic", label: "Realistic" },
  { value: "artistic", label: "Artistic" },
  { value: "ugc", label: "UGC Style" },
];

// Voice options
const VOICE_OPTIONS = [
  { value: "female-american-rachel", label: "Rachel (Female, American)" },
  { value: "female-british-alice", label: "Alice (Female, British)" },
  { value: "male-american-james", label: "James (Male, American)" },
  { value: "male-british-george", label: "George (Male, British)" },
  { value: "none", label: "No Voice" },
];

// Video modes
const VIDEO_MODES = [
  { value: "standard", label: "Standard" },
  { value: "fast", label: "Fast (Kling)" },
  { value: "premium", label: "Premium (fal.ai)" },
];

// Logo positions
const LOGO_POSITIONS = [
  { value: "top-left", label: "Top Left" },
  { value: "top-right", label: "Top Right" },
  { value: "bottom-left", label: "Bottom Left" },
  { value: "bottom-right", label: "Bottom Right" },
];

// Logo sizes
const LOGO_SIZES = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
];

export default function BrandKitPage() {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brandKit, setBrandKit] = useState(null);
  const [activeTab, setActiveTab] = useState("branding");
  
  // File upload refs
  const logoInputRef = useRef(null);
  const introInputRef = useRef(null);
  const outroInputRef = useRef(null);
  
  // Upload states
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingIntro, setUploadingIntro] = useState(false);
  const [uploadingOutro, setUploadingOutro] = useState(false);
  
  // Hashtag input
  const [newHashtag, setNewHashtag] = useState("");
  
  // Template modal
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    description: "",
    video_mode: "standard",
    video_style: "cinematic",
    voice_option: "female-american-rachel",
    tone: "friendly",
    brand_voice: "casual",
    duration: null,
    hashtags: [],
    visual_description: "",
    use_logo: true,
    use_intro: false,
    use_outro: false,
  });
  
  // Cloned voices state - NEW
  const [clonedVoices, setClonedVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  
  // Combined voice options (standard + cloned)
  const allVoiceOptions = [
    ...VOICE_OPTIONS,
    ...clonedVoices.map(v => ({
      value: `cloned:${v.voice_id}`,
      label: `${v.name} (My Voice)`,
      isCloned: true
    }))
  ];

  // Fetch brand kit on mount
  useEffect(() => {
    fetchBrandKit();
    fetchClonedVoices();
  }, []);
  
  // Fetch user's cloned voices
  const fetchClonedVoices = async () => {
    setLoadingVoices(true);
    try {
      const response = await fetch(`${BASE_URL}/api/voices/my-voices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setClonedVoices(data.voices || []);
      }
    } catch (error) {
      console.error("Failed to fetch cloned voices:", error);
    } finally {
      setLoadingVoices(false);
    }
  };

  const fetchBrandKit = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/brand-kit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setBrandKit(data);
    } catch (error) {
      console.error("Failed to fetch brand kit:", error);
      toast.error("Failed to load brand kit");
    } finally {
      setLoading(false);
    }
  };

  const saveBrandKit = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BASE_URL}/api/brand-kit`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(brandKit),
      });
      
      if (response.ok) {
        const data = await response.json();
        setBrandKit(data);
        toast.success("Brand kit saved!");
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      console.error("Failed to save brand kit:", error);
      toast.error("Failed to save brand kit");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("position", brandKit?.logo_position || "bottom-right");
    formData.append("opacity", brandKit?.logo_opacity || 0.8);
    formData.append("size", brandKit?.logo_size || "small");
    
    try {
      const response = await fetch(`${BASE_URL}/api/brand-kit/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setBrandKit(data.brand_kit);
        toast.success("Logo uploaded!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleIntroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingIntro(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("duration", brandKit?.intro_duration || 3);
    
    try {
      const response = await fetch(`${BASE_URL}/api/brand-kit/intro`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setBrandKit(prev => ({ ...prev, intro_video_url: data.intro_url }));
        toast.success("Intro video uploaded!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Intro upload error:", error);
      toast.error("Failed to upload intro video");
    } finally {
      setUploadingIntro(false);
    }
  };

  const handleOutroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingOutro(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("duration", brandKit?.outro_duration || 3);
    
    try {
      const response = await fetch(`${BASE_URL}/api/brand-kit/outro`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setBrandKit(prev => ({ ...prev, outro_video_url: data.outro_url }));
        toast.success("Outro video uploaded!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Outro upload error:", error);
      toast.error("Failed to upload outro video");
    } finally {
      setUploadingOutro(false);
    }
  };

  const addHashtag = () => {
    if (!newHashtag.trim()) return;
    const tag = newHashtag.startsWith("#") ? newHashtag : `#${newHashtag}`;
    if (!brandKit.default_hashtags?.includes(tag)) {
      setBrandKit(prev => ({
        ...prev,
        default_hashtags: [...(prev.default_hashtags || []), tag]
      }));
    }
    setNewHashtag("");
  };

  const removeHashtag = (tag) => {
    setBrandKit(prev => ({
      ...prev,
      default_hashtags: prev.default_hashtags?.filter(t => t !== tag) || []
    }));
  };

  const saveTemplate = async () => {
    if (!newTemplate.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    
    try {
      const response = await fetch(`${BASE_URL}/api/brand-kit/templates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newTemplate),
      });
      
      if (response.ok) {
        const data = await response.json();
        setBrandKit(prev => ({
          ...prev,
          templates: [...(prev.templates || []), data.template]
        }));
        setShowTemplateModal(false);
        setNewTemplate({
          name: "",
          description: "",
          video_mode: "standard",
          video_style: "cinematic",
          voice_option: "female-american-rachel",
          tone: "friendly",
          brand_voice: "casual",
          duration: null,
          hashtags: [],
          visual_description: "",
          use_logo: true,
          use_intro: false,
          use_outro: false,
        });
        toast.success("Template saved!");
      } else {
        throw new Error("Failed to save template");
      }
    } catch (error) {
      console.error("Save template error:", error);
      toast.error("Failed to save template");
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      const response = await fetch(`${BASE_URL}/api/brand-kit/templates/${templateId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        setBrandKit(prev => ({
          ...prev,
          templates: prev.templates?.filter(t => t.id !== templateId) || []
        }));
        toast.success("Template deleted");
      } else {
        throw new Error("Failed to delete");
      }
    } catch (error) {
      console.error("Delete template error:", error);
      toast.error("Failed to delete template");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-6 space-y-6 max-w-6xl mx-auto"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Palette className="w-8 h-8 text-primary" />
            Brand Kit
          </h1>
          <p className="text-muted-foreground mt-1">
            Customize your brand identity and default video settings
          </p>
        </div>
        <Button
          onClick={saveBrandKit}
          disabled={saving}
          className="bg-primary hover:bg-primary/90"
          data-testid="save-brand-kit-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={itemVariants}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-secondary/50">
            <TabsTrigger value="branding" className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="colors" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Colors
            </TabsTrigger>
            <TabsTrigger value="defaults" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Defaults
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <Layout className="w-4 h-4" />
              Templates
            </TabsTrigger>
          </TabsList>

          {/* Branding Tab */}
          <TabsContent value="branding" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Brand Name */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Type className="w-5 h-5 text-primary" />
                    Brand Name
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    value={brandKit?.brand_name || ""}
                    onChange={(e) => setBrandKit(prev => ({ ...prev, brand_name: e.target.value }))}
                    placeholder="Your brand name"
                    className="bg-secondary/50"
                    data-testid="brand-name-input"
                  />
                </CardContent>
              </Card>

              {/* Logo Upload */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-primary" />
                    Brand Logo
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    type="file"
                    ref={logoInputRef}
                    onChange={handleLogoUpload}
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                  />
                  
                  {brandKit?.logo_url ? (
                    <div className="flex items-center gap-4">
                      <img
                        src={brandKit.logo_url}
                        alt="Brand Logo"
                        className="w-20 h-20 object-contain bg-secondary rounded-lg"
                      />
                      <Button
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Change Logo
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        className="w-full h-24 border-dashed"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        data-testid="upload-logo-btn"
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-6 h-6 animate-spin" />
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="w-6 h-6" />
                            <span>Upload Logo</span>
                            <span className="text-xs text-muted-foreground">PNG, JPEG, WebP, SVG</span>
                          </div>
                        )}
                      </Button>
                      <MediaPickerButton
                        onSelect={(data) => {
                          if (data.url || data.thumbnail_url) {
                            const imageUrl = data.url || data.thumbnail_url;
                            setBrandKit(prev => ({ ...prev, logo_url: imageUrl }));
                            toast.success("Logo selected from Media Library");
                          } else if (data.isUpload && data.file) {
                            // Trigger upload through existing flow
                            const dataTransfer = new DataTransfer();
                            dataTransfer.items.add(data.file);
                            logoInputRef.current.files = dataTransfer.files;
                            logoInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
                          }
                        }}
                        mediaType="image"
                        buttonText="Pick from Media Library"
                        buttonClassName="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-sm text-purple-300 transition-colors"
                      />
                    </div>
                  )}
                  
                  {/* Logo Settings */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Position</Label>
                      <Select
                        value={brandKit?.logo_position || "bottom-right"}
                        onValueChange={(v) => setBrandKit(prev => ({ ...prev, logo_position: v }))}
                      >
                        <SelectTrigger className="bg-secondary/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LOGO_POSITIONS.map(pos => (
                            <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Size</Label>
                      <Select
                        value={brandKit?.logo_size || "small"}
                        onValueChange={(v) => setBrandKit(prev => ({ ...prev, logo_size: v }))}
                      >
                        <SelectTrigger className="bg-secondary/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LOGO_SIZES.map(size => (
                            <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Opacity: {Math.round((brandKit?.logo_opacity || 0.8) * 100)}%</Label>
                    <Slider
                      value={[(brandKit?.logo_opacity || 0.8) * 100]}
                      onValueChange={([v]) => setBrandKit(prev => ({ ...prev, logo_opacity: v / 100 }))}
                      min={10}
                      max={100}
                      step={5}
                      className="py-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Intro Video */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileVideo className="w-5 h-5 text-green-500" />
                    Intro Video
                  </CardTitle>
                  <CardDescription>Auto-prepend to your videos</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    type="file"
                    ref={introInputRef}
                    onChange={handleIntroUpload}
                    accept="video/mp4,video/webm,video/quicktime"
                    className="hidden"
                  />
                  
                  {brandKit?.intro_video_url ? (
                    <div className="space-y-3">
                      <video
                        src={brandKit.intro_video_url}
                        className="w-full h-32 object-cover rounded-lg bg-secondary"
                        controls
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => introInputRef.current?.click()}
                        disabled={uploadingIntro}
                      >
                        {uploadingIntro ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Change
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        className="w-full h-20 border-dashed"
                        onClick={() => introInputRef.current?.click()}
                        disabled={uploadingIntro}
                      >
                        {uploadingIntro ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <Upload className="w-5 h-5" />
                            <span className="text-sm">Upload Intro</span>
                          </div>
                        )}
                      </Button>
                      <MediaPickerButton
                        onSelect={(data) => {
                          if (data.url) {
                            setBrandKit(prev => ({ ...prev, intro_video_url: data.url }));
                            toast.success("Intro video selected from Media Library");
                          }
                        }}
                        mediaType="video"
                        buttonText="Pick from Media Library"
                        buttonClassName="w-full flex items-center justify-center gap-2 px-2 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-xs text-purple-300 transition-colors"
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Duration: {brandKit?.intro_duration || 3}s</Label>
                    <Slider
                      value={[brandKit?.intro_duration || 3]}
                      onValueChange={([v]) => setBrandKit(prev => ({ ...prev, intro_duration: v }))}
                      min={1}
                      max={10}
                      step={1}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Outro Video */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileVideo className="w-5 h-5 text-orange-500" />
                    Outro Video
                  </CardTitle>
                  <CardDescription>Auto-append to your videos</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    type="file"
                    ref={outroInputRef}
                    onChange={handleOutroUpload}
                    accept="video/mp4,video/webm,video/quicktime"
                    className="hidden"
                  />
                  
                  {brandKit?.outro_video_url ? (
                    <div className="space-y-3">
                      <video
                        src={brandKit.outro_video_url}
                        className="w-full h-32 object-cover rounded-lg bg-secondary"
                        controls
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => outroInputRef.current?.click()}
                        disabled={uploadingOutro}
                      >
                        {uploadingOutro ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Change
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full h-20 border-dashed"
                      onClick={() => outroInputRef.current?.click()}
                      disabled={uploadingOutro}
                    >
                      {uploadingOutro ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <Upload className="w-5 h-5" />
                          <span className="text-sm">Upload Outro</span>
                        </div>
                      )}
                    </Button>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Duration: {brandKit?.outro_duration || 3}s</Label>
                    <Slider
                      value={[brandKit?.outro_duration || 3]}
                      onValueChange={([v]) => setBrandKit(prev => ({ ...prev, outro_duration: v }))}
                      min={1}
                      max={10}
                      step={1}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Typography */}
            <Card className="bg-card/50 border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Type className="w-5 h-5 text-primary" />
                  Typography
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Heading Font</Label>
                    <Select
                      value={brandKit?.heading_font || "Inter"}
                      onValueChange={(v) => setBrandKit(prev => ({ ...prev, heading_font: v }))}
                    >
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONTS.map(font => (
                          <SelectItem key={font} value={font} style={{ fontFamily: font }}>{font}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Body Font</Label>
                    <Select
                      value={brandKit?.body_font || "Inter"}
                      onValueChange={(v) => setBrandKit(prev => ({ ...prev, body_font: v }))}
                    >
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONTS.map(font => (
                          <SelectItem key={font} value={font} style={{ fontFamily: font }}>{font}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Colors Tab */}
          <TabsContent value="colors" className="space-y-6 mt-6">
            <Card className="bg-card/50 border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette className="w-5 h-5 text-primary" />
                  Brand Colors
                </CardTitle>
                <CardDescription>Set your brand's color palette for videos and captions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Primary Color */}
                  <div className="space-y-3">
                    <Label>Primary Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={brandKit?.primary_color || "#A855F7"}
                        onChange={(e) => setBrandKit(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                      />
                      <Input
                        value={brandKit?.primary_color || "#A855F7"}
                        onChange={(e) => setBrandKit(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="bg-secondary/50 flex-1"
                        data-testid="primary-color-input"
                      />
                    </div>
                  </div>

                  {/* Secondary Color */}
                  <div className="space-y-3">
                    <Label>Secondary Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={brandKit?.secondary_color || "#10B981"}
                        onChange={(e) => setBrandKit(prev => ({ ...prev, secondary_color: e.target.value }))}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                      />
                      <Input
                        value={brandKit?.secondary_color || "#10B981"}
                        onChange={(e) => setBrandKit(prev => ({ ...prev, secondary_color: e.target.value }))}
                        className="bg-secondary/50 flex-1"
                      />
                    </div>
                  </div>

                  {/* Accent Color */}
                  <div className="space-y-3">
                    <Label>Accent Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={brandKit?.accent_color || "#F59E0B"}
                        onChange={(e) => setBrandKit(prev => ({ ...prev, accent_color: e.target.value }))}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                      />
                      <Input
                        value={brandKit?.accent_color || "#F59E0B"}
                        onChange={(e) => setBrandKit(prev => ({ ...prev, accent_color: e.target.value }))}
                        className="bg-secondary/50 flex-1"
                      />
                    </div>
                  </div>

                  {/* Text Color */}
                  <div className="space-y-3">
                    <Label>Text Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={brandKit?.text_color || "#FFFFFF"}
                        onChange={(e) => setBrandKit(prev => ({ ...prev, text_color: e.target.value }))}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                      />
                      <Input
                        value={brandKit?.text_color || "#FFFFFF"}
                        onChange={(e) => setBrandKit(prev => ({ ...prev, text_color: e.target.value }))}
                        className="bg-secondary/50 flex-1"
                      />
                    </div>
                  </div>

                  {/* Background Color */}
                  <div className="space-y-3">
                    <Label>Background Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={brandKit?.background_color || "#0A0A0B"}
                        onChange={(e) => setBrandKit(prev => ({ ...prev, background_color: e.target.value }))}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                      />
                      <Input
                        value={brandKit?.background_color || "#0A0A0B"}
                        onChange={(e) => setBrandKit(prev => ({ ...prev, background_color: e.target.value }))}
                        className="bg-secondary/50 flex-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Color Preview */}
                <div className="mt-8">
                  <Label className="mb-3 block">Preview</Label>
                  <div 
                    className="p-6 rounded-lg border border-border"
                    style={{ backgroundColor: brandKit?.background_color || "#0A0A0B" }}
                  >
                    <h3 
                      className="text-2xl font-bold mb-2"
                      style={{ 
                        color: brandKit?.primary_color || "#A855F7",
                        fontFamily: brandKit?.heading_font || "Inter"
                      }}
                    >
                      {brandKit?.brand_name || "Your Brand Name"}
                    </h3>
                    <p 
                      style={{ 
                        color: brandKit?.text_color || "#FFFFFF",
                        fontFamily: brandKit?.body_font || "Inter"
                      }}
                    >
                      This is how your brand colors will look in your videos.
                    </p>
                    <div className="flex gap-2 mt-4">
                      <Badge style={{ backgroundColor: brandKit?.primary_color || "#A855F7" }}>Primary</Badge>
                      <Badge style={{ backgroundColor: brandKit?.secondary_color || "#10B981" }}>Secondary</Badge>
                      <Badge style={{ backgroundColor: brandKit?.accent_color || "#F59E0B" }}>Accent</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Defaults Tab */}
          <TabsContent value="defaults" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Video Settings */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Video className="w-5 h-5 text-primary" />
                    Default Video Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Default Video Mode</Label>
                    <Select
                      value={brandKit?.default_video_mode || "standard"}
                      onValueChange={(v) => setBrandKit(prev => ({ ...prev, default_video_mode: v }))}
                    >
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VIDEO_MODES.map(mode => (
                          <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Video Style</Label>
                    <Select
                      value={brandKit?.default_video_style || "cinematic"}
                      onValueChange={(v) => setBrandKit(prev => ({ ...prev, default_video_style: v }))}
                    >
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VIDEO_STYLES.map(style => (
                          <SelectItem key={style.value} value={style.value}>{style.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Voice</Label>
                    <Select
                      value={brandKit?.default_voice || "female-american-rachel"}
                      onValueChange={(v) => setBrandKit(prev => ({ ...prev, default_voice: v }))}
                    >
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {allVoiceOptions.map(voice => (
                          <SelectItem key={voice.value} value={voice.value}>
                            {voice.label}
                            {voice.isCloned && <span className="ml-2 text-xs text-primary">★</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Content Settings */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Default Content Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Default Tone</Label>
                    <Select
                      value={brandKit?.default_tone || "friendly"}
                      onValueChange={(v) => setBrandKit(prev => ({ ...prev, default_tone: v }))}
                    >
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="enthusiastic">Enthusiastic</SelectItem>
                        <SelectItem value="authoritative">Authoritative</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Brand Voice</Label>
                    <Select
                      value={brandKit?.default_brand_voice || "casual"}
                      onValueChange={(v) => setBrandKit(prev => ({ ...prev, default_brand_voice: v }))}
                    >
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="playful">Playful</SelectItem>
                        <SelectItem value="inspirational">Inspirational</SelectItem>
                        <SelectItem value="educational">Educational</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Default Hashtags */}
              <Card className="bg-card/50 border-border/50 md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Hash className="w-5 h-5 text-primary" />
                    Default Hashtags
                  </CardTitle>
                  <CardDescription>These will be auto-added to all your videos</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      value={newHashtag}
                      onChange={(e) => setNewHashtag(e.target.value)}
                      placeholder="Add a hashtag..."
                      className="bg-secondary/50"
                      onKeyDown={(e) => e.key === "Enter" && addHashtag()}
                      data-testid="hashtag-input"
                    />
                    <Button onClick={addHashtag} variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {brandKit?.default_hashtags?.map((tag, i) => (
                      <Badge
                        key={i}
                        variant="secondary"
                        className="pl-3 pr-1 py-1 flex items-center gap-1"
                      >
                        {tag}
                        <button
                          onClick={() => removeHashtag(tag)}
                          className="ml-1 hover:bg-destructive/20 rounded p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!brandKit?.default_hashtags || brandKit.default_hashtags.length === 0) && (
                      <span className="text-muted-foreground text-sm">No default hashtags set</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-6 mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Video Templates</h3>
                <p className="text-muted-foreground text-sm">Save and reuse video configurations</p>
              </div>
              <Button onClick={() => setShowTemplateModal(true)} data-testid="create-template-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {brandKit?.templates?.map((template) => (
                <Card key={template.id} className="bg-card/50 border-border/50">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        {template.description && (
                          <CardDescription className="text-xs mt-1">{template.description}</CardDescription>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => deleteTemplate(template.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <div className="flex justify-between">
                        <span>Mode:</span>
                        <span className="text-foreground">{template.video_mode}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Style:</span>
                        <span className="text-foreground">{template.video_style}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Voice:</span>
                        <span className="text-foreground truncate max-w-[120px]">{template.voice_option}</span>
                      </div>
                      {template.hashtags?.length > 0 && (
                        <div className="pt-2 flex flex-wrap gap-1">
                          {template.hashtags.slice(0, 3).map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] px-1">{tag}</Badge>
                          ))}
                          {template.hashtags.length > 3 && (
                            <Badge variant="outline" className="text-[10px] px-1">+{template.hashtags.length - 3}</Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {(!brandKit?.templates || brandKit.templates.length === 0) && (
                <Card className="bg-card/30 border-dashed col-span-full">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Layout className="w-12 h-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground text-center">
                      No templates yet. Create one to save your favorite video settings!
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Template Creation Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card rounded-lg p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold">Create Template</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowTemplateModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Product Launch Template"
                  className="bg-secondary/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Short description..."
                  className="bg-secondary/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Video Mode</Label>
                  <Select
                    value={newTemplate.video_mode}
                    onValueChange={(v) => setNewTemplate(prev => ({ ...prev, video_mode: v }))}
                  >
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VIDEO_MODES.map(mode => (
                        <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Video Style</Label>
                  <Select
                    value={newTemplate.video_style}
                    onValueChange={(v) => setNewTemplate(prev => ({ ...prev, video_style: v }))}
                  >
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VIDEO_STYLES.map(style => (
                        <SelectItem key={style.value} value={style.value}>{style.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Voice</Label>
                <Select
                  value={newTemplate.voice_option}
                  onValueChange={(v) => setNewTemplate(prev => ({ ...prev, voice_option: v }))}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allVoiceOptions.map(voice => (
                      <SelectItem key={voice.value} value={voice.value}>
                        {voice.label}
                        {voice.isCloned && <span className="ml-2 text-xs text-primary">★</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newTemplate.use_logo}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, use_logo: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Use Logo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newTemplate.use_intro}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, use_intro: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Use Intro</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newTemplate.use_outro}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, use_outro: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Use Outro</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
                Cancel
              </Button>
              <Button onClick={saveTemplate}>
                <Save className="w-4 h-4 mr-2" />
                Save Template
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
